import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { ProjectView } from "@portal/schemas";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  NativeSelect,
  NativeSelectOption,
} from "@portal/ui";
import { ChevronDown, ListChecks, Plus } from "lucide-react";
import { useState } from "react";
import { useProjects } from "../../api/queries";
import { CardStatus } from "../../components/cards/CardStatus";
import { DataTable, createDataTableColumns } from "../../components/DataTable";
import { CreateProjectDialog } from "./CreateProjectDialog";
import { PROJECT_STATUS_LABELS, ProjectStatusBadge } from "./ProjectStatusBadge";

const col = createDataTableColumns<ProjectView>();
const columns = col.columns([
  col.accessor("name", {
    header: "名前",
    cell: (info) => (
      <Link
        to="/projects/$projectId"
        params={{ projectId: info.row.original.id }}
        className="font-medium hover:underline"
      >
        {info.getValue()}
      </Link>
    ),
  }),
  col.accessor((p) => p.code ?? "", { id: "code", header: "コード" }),
  col.accessor((p) => p.customers.find((c) => c.isPrimary)?.customerName ?? "", {
    id: "primaryCustomer",
    header: "主顧客",
  }),
  col.accessor((p) => PROJECT_STATUS_LABELS[p.status], {
    id: "status",
    header: "状態",
    cell: (info) => <ProjectStatusBadge status={info.row.original.status} />,
  }),
  col.accessor((p) => p.participants.filter((m) => m.active).length, {
    id: "participants",
    header: "参加者",
    cell: (info) => `${info.getValue()} 名`,
  }),
]);
const FILTERS = [{ columnId: "primaryCustomer", label: "主顧客" }] as const;

export function ProjectsPage() {
  const search = useSearch({ from: "/app/projects" });
  const navigate = useNavigate();
  const projects = useProjects(search.status ? { status: search.status } : {});

  return (
    <Card data-testid="projects-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>プロジェクト一覧</CardTitle>
        <div className="flex items-center gap-3">
          <NativeSelect
            aria-label="状態で絞り込み"
            value={search.status ?? ""}
            onChange={(e) =>
              navigate({
                to: "/projects",
                search: e.target.value
                  ? { status: e.target.value as keyof typeof PROJECT_STATUS_LABELS }
                  : {},
              })
            }
          >
            <NativeSelectOption value="">すべて</NativeSelectOption>
            {Object.entries(PROJECT_STATUS_LABELS).map(([k, v]) => (
              <NativeSelectOption key={k} value={k}>
                {v}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <NewProjectButton />
        </div>
      </CardHeader>
      <CardContent>
        <CardStatus
          isPending={projects.isPending}
          isError={projects.isError}
          refetch={() => projects.refetch()}
        />
        {projects.data && (
          <DataTable
            columns={columns}
            data={projects.data.projects}
            getRowId={(p) => p.id}
            emptyText="プロジェクトはありません。"
            searchPlaceholder="名前・コード・顧客で検索"
            filters={FILTERS}
            rowProjectId={(p) => p.id}
          />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * 「新規プロジェクト」と、その右の ▼(ほかの作り方)をくっつけた分割ボタン(ユーザー要望 2026-10-15)。
 * ▼ のメニューに「候補から作成」を置く(ナビの「候補」タブはやめた)。shadcn/ui の ButtonGroup は未取り込みで、
 * 中身はボタンを並べて角をつなげるだけなので、既存の Button と DropdownMenu を組み合わせる。
 */
function NewProjectButton() {
  const [creating, setCreating] = useState(false);
  return (
    <div className="flex">
      <Button className="rounded-r-none" onClick={() => setCreating(true)}>
        <Plus />
        新規プロジェクト
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* メニューが出ている間は、押された見た目にする(少し明るく、内側に影、▼ を上向きに。2026-10-15) */}
          <Button
            className="group rounded-l-none border-l border-l-primary-foreground/30 px-2 data-[state=open]:bg-primary/75 data-[state=open]:shadow-[inset_0_2px_4px_rgb(0_0_0/0.45)]"
            aria-label="ほかの作り方"
            title="ほかの作り方"
          >
            <ChevronDown className="transition-transform group-data-[state=open]:rotate-180" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          <DropdownMenuItem asChild>
            <Link to="/projects/candidates">
              <ListChecks />
              候補から作成
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CreateProjectDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}
